import {
  MagnifyingGlassIcon,
  PlusCircleIcon,
} from "@heroicons/react/24/outline";
import React, { useRef, useState } from "react";

import type {
  BomItem,
  PaginatedBomItems,
  RecipeFormValues,
} from "../../../features/recipes/recipeTypes";

interface RecipeBomItemsTableProps {
  bomItems: PaginatedBomItems;
  bomItemsLoading: boolean;
  getBomItems: (search: string, page: number) => void;
  onAddBomToIngredient: (item: BomItem) => void;
  recipe: RecipeFormValues;
}

export default function RecipeBomItemsTable({
  bomItems,
  bomItemsLoading,
  getBomItems,
  onAddBomToIngredient,
  recipe,
}: RecipeBomItemsTableProps) {
  const searchRef = useRef<HTMLInputElement>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const recipeSaved = Number(recipe.id) >= 1;

  const handleSearch = () => {
    const search = searchRef.current?.value ?? "";
    setHasSearched(search.length > 0);
    getBomItems(search, 0);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col px-1">
      <h3 className="text-base font-semibold leading-6 text-gray-900">
        Choose a bom item
      </h3>
      <div className="mt-2 flex rounded-md">
        <input
          type="search"
          name="searchInput"
          id="searchInput"
          className="block w-full rounded-l-md border border-gray-300 pl-2 sm:text-sm"
          placeholder="Search by name or plu"
          ref={searchRef}
          disabled={!recipeSaved}
          onKeyDown={(event) => {
            if (event.key === "Enter") handleSearch();
          }}
        />
        <button
          type="button"
          className="relative -ml-px inline-flex items-center space-x-2 rounded-r-md border border-gray-300 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-1"
          onClick={handleSearch}
          disabled={!recipeSaved}
        >
          <MagnifyingGlassIcon
            className="h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
        </button>
      </div>
      <div className="relative mb-2 mt-6 flex-1 overflow-x-auto">
        <div className="card-custom overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-white">
              <tr>
                <th
                  scope="col"
                  className="py-1 pl-3 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                >
                  Bom Plu
                </th>
                <th
                  scope="col"
                  className="px-3 py-1 text-left text-sm font-semibold text-gray-900"
                >
                  Name
                </th>
                <th
                  scope="col"
                  className="px-3 py-1 text-left text-sm font-semibold text-gray-900"
                >
                  Type
                </th>
                <th
                  scope="col"
                  className="px-3 py-1 text-left text-sm font-semibold text-gray-900"
                ></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {bomItemsLoading ? (
                <tr>
                  <td
                    className="whitespace-nowrap py-1 pl-4 pr-3 text-center text-sm font-medium text-gray-500 sm:pl-6"
                    colSpan={4}
                  >
                    Loading ...
                  </td>
                </tr>
              ) : bomItems.data.length === 0 ? (
                <tr>
                  <td
                    className="whitespace-nowrap py-1 pl-4 pr-3 text-center text-sm font-medium text-gray-500 sm:pl-6"
                    colSpan={4}
                  >
                    {hasSearched
                      ? "We could not find that BOM item"
                      : "Please search for a BOM item"}
                  </td>
                </tr>
              ) : (
                bomItems.data.map((singleBomItem) => (
                  <tr key={singleBomItem.id}>
                    <td className="whitespace-nowrap py-1 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6">
                      {singleBomItem.plu}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1 text-sm text-gray-500">
                      {singleBomItem.name}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1 text-sm text-gray-500">
                      {singleBomItem.product_type?.label}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1 text-sm text-gray-500">
                      <button
                        type="button"
                        className="relative -ml-px inline-flex items-center space-x-2 rounded-md border border-gray-300 bg-indigo-50 px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-100 focus:outline-none focus:ring-1"
                        onClick={() => onAddBomToIngredient(singleBomItem)}
                      >
                        <PlusCircleIcon
                          className="h-5 w-5 text-gray-400"
                          aria-hidden="true"
                        />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
